import {
  ApiResponse,
  ClusterListResponse,
  Configuration as IsolationUnitsConfiguration,
  DefaultApi as IsolationUnitsDefaultApi,
  IsolationUnit,
  IsolationUnitListResponse,
  PartitionListResponse,
  ResponseError,
} from '@internal/plugin-neo4j-k8s-clusters-common';
import {
  ApiRef,
  createApiRef,
  DiscoveryApi,
  FetchApi,
} from '@backstage/frontend-plugin-api';
import {
  Configuration as TrafficConfigConfiguration,
  TrafficConfigApi,
  type TrafficconfigTrafficConfig,
  V1IsolationUnitsIsolationUnitRegionsRegionTrafficConfigPutRequest,
} from '@internal/backstage-plugin-neo4j-networking-common';

import type {
  K8sClustersApi,
  K8sClustersApiResponse,
  LogForwardingConfig,
  TrafficConfigMap,
} from './types';

async function formatApiResponse<T>(
  result: ApiResponse<T>,
): Promise<K8sClustersApiResponse<T>> {
  try {
    return {
      message: result.raw.statusText,
      ok: result.raw.ok,
      statusCode: result.raw.status,
      value: await result.value(),
    };
  } catch (error) {
    if (error instanceof ResponseError) {
      return {
        message: error.message,
        ok: false,
        statusCode: error.response.status,
      };
    }

    return {
      message: error instanceof Error ? error.message : 'Unknown error',
      ok: false,
      statusCode: 400,
    };
  }
}

async function parseJsonResponse<T>(
  response: Response,
): Promise<K8sClustersApiResponse<T>> {
  try {
    const repsonseJson = await response.json();

    return {
      message: repsonseJson.message || response.statusText,
      ok: response.ok,
      statusCode: response.status,
      value: repsonseJson,
    };
  } catch (error) {
    return {
      ok: response.ok,
      statusCode: response.status,
      message: error instanceof Error ? error.message : response.statusText,
    };
  }
}

export class K8sClustersRestApi implements K8sClustersApi {
  private readonly discoveryApi: DiscoveryApi;
  private readonly fetchApi: FetchApi;

  constructor(options: { discoveryApi: DiscoveryApi; fetchApi: FetchApi }) {
    this.discoveryApi = options.discoveryApi;
    this.fetchApi = options.fetchApi;
  }

  /**
   * ISOLATION UNITS API
   */

  async getIsolationUnitsApiConfig() {
    const baseUrl = await this.discoveryApi.getBaseUrl('proxy');

    return new IsolationUnitsConfiguration({
      basePath: `${baseUrl}/isolation-units-api`,
      fetchApi: this.fetchApi.fetch,
    });
  }

  async getIsolationUnitsApi() {
    const config = await this.getIsolationUnitsApiConfig();

    return new IsolationUnitsDefaultApi(config);
  }

  async listIsolationUnits(): Promise<
    K8sClustersApiResponse<IsolationUnitListResponse>
  > {
    const isolationUnitsApi = await this.getIsolationUnitsApi();
    const result = await isolationUnitsApi.v1IsolationunitsGetRaw();

    return formatApiResponse(result);
  }

  async listClusters(): Promise<K8sClustersApiResponse<ClusterListResponse>> {
    const isolationUnitsApi = await this.getIsolationUnitsApi();
    const result = await isolationUnitsApi.v1ClustersGetRaw();

    return formatApiResponse(result);
  }

  async listPartitions(): Promise<
    K8sClustersApiResponse<PartitionListResponse>
  > {
    const isolationUnitsApi = await this.getIsolationUnitsApi();
    const result = await isolationUnitsApi.v1PartitionsGetRaw();

    return formatApiResponse(result);
  }

  async getIsolationUnit(
    isolationUnitId: string,
  ): Promise<K8sClustersApiResponse<IsolationUnit>> {
    const isolationUnitsApi = await this.getIsolationUnitsApi();

    const result = await isolationUnitsApi.v1IsolationunitsIdGetRaw({
      id: isolationUnitId,
    });

    return formatApiResponse(result);
  }

  /**
   * TRAFFIC CONFIG API
   */

  async getTrafficConfigApiConfig() {
    const baseUrl = await this.discoveryApi.getBaseUrl('proxy');

    return new TrafficConfigConfiguration({
      basePath: `${baseUrl}/traffic-config-api`,
      fetchApi: this.fetchApi.fetch,
    });
  }

  async getTrafficConfigApi() {
    const config = await this.getTrafficConfigApiConfig();

    return new TrafficConfigApi(config);
  }

  async getTrafficConfig(orchestraId: string) {
    const api = await this.getTrafficConfigApi();

    const response = await api.v1OrchestrasOrchestraTrafficConfigGetRaw({
      orchestra: orchestraId,
    });

    return formatApiResponse(response);
  }

  async getTrafficConfigs(): Promise<TrafficConfigMap> {
    const baseUrl = await this.discoveryApi.getBaseUrl('neo4j-k8s-clusters');

    const response = await this.fetchApi.fetch(`${baseUrl}/traffic-configs`, {
      headers: { Accept: 'application/json' },
    });

    if (!response.ok) {
      throw new Error(
        `Failed to fetch traffic configs: ${response.status} ${response.statusText}`,
      );
    }

    return response.json();
  }

  async updateTrafficConfig(
    params: V1IsolationUnitsIsolationUnitRegionsRegionTrafficConfigPutRequest,
  ): Promise<K8sClustersApiResponse<TrafficconfigTrafficConfig>> {
    const { isolationUnit, region, trafficConfig } = params;

    const baseUrl = await this.discoveryApi.getBaseUrl('neo4j-k8s-clusters');

    const response = await this.fetchApi.fetch(
      `${baseUrl}/traffic-config/${encodeURIComponent(isolationUnit)}/${encodeURIComponent(region)}`,
      {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(trafficConfig),
      },
    );

    return parseJsonResponse<TrafficconfigTrafficConfig>(response);
  }

  /**
   * LOG FORWARDING API
   */

  async listOrchestraLogForwardingConfigs(
    orchestraId: string,
  ): Promise<K8sClustersApiResponse<LogForwardingConfig[]>> {
    const trimmedOrchestraId = orchestraId.trim();
    if (!trimmedOrchestraId) {
      return {
        ok: false,
        statusCode: 400,
        message: 'Orchestra ID is required',
      };
    }

    const baseUrl = await this.discoveryApi.getBaseUrl('proxy');

    const response = await this.fetchApi.fetch(
      `${baseUrl}/log-forwarding-api/v1/orchestras/${encodeURIComponent(trimmedOrchestraId)}/log-forwarding`,
      {
        headers: {
          Accept: 'application/json',
        },
      },
    );

    return parseJsonResponse<LogForwardingConfig[]>(response);
  }
}

export const k8sClustersApiRef: ApiRef<K8sClustersApi> =
  createApiRef<K8sClustersApi>({
    id: 'plugin.neo4j-k8s-clusters-react.service',
  });
